import React, { useState, useEffect } from 'react';
import { Portal, Dialog, Flex, Box } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToastContext } from './UI/ToastManager';
import { graphqlRequest } from '../utils/graphqlRequest';
import { createProgramMutation, updateProgramMutation, createProgramConstrainedMutation, updateProgramConstrainedMutation } from '../constants/queries';
import { useProgramByIdQuery } from '../query/useProgramByIdQuery';
import { useProgramConstrainedByIdQuery } from '../query/useProgramConstrainedByIdQuery';
import ProgramTopBar from './ProgramTopBar';
import ProgramTreePanel from './ProgramTreePanel';
import ProgramFormPanel, { buildProgramMutationPayload, buildProgramConstrainedMutationPayload } from './ProgramFormPanel';
import ExerciseTreePanel from './ExerciseTreePanel';
import ExerciseFormPanel from './ExerciseFormPanel';
import { constraintsObjectToArray } from '../utils/constraintFormat';
import { uploadMedia } from '../utils/mediaUpload';
import { uploadVideoMultipart } from '../query/useUploadVideoMutation';

interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
}

interface ProgramEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programData?: any;
}

// Helper to find a node by id in the tree
function findNodeById(nodes: TreeNode[], id: string | null): TreeNode | null {
  if (!id) return null;
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

const ProgramEditDialog: React.FC<ProgramEditDialogProps> = ({ open, onOpenChange, programData }) => {
  const { showSuccess, showError } = useToastContext();
  const queryClient = useQueryClient();
  
  // State for draft/published
  const [isPublished, setIsPublished] = useState(false);
  // State for tree data
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  // State for selected node
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // State for loading
  const [isSaving, setIsSaving] = useState(false);

  // Placeholder for form data and onChange
  const [formData, setFormData] = useState<any>({});
  
  // Local data for steps and step options
  const [localStepData, setLocalStepData] = useState<Record<string, any>>({});
  const [localStepOptionData, setLocalStepOptionData] = useState<Record<string, any>>({});
  
  // Local data for program
  const [localProgramData, setLocalProgramData] = useState<any>({});

  // Exercise-related state
  const [exerciseTreeData, setExerciseTreeData] = useState<TreeNode[]>([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(null);
  const [exerciseFormData, setExerciseFormData] = useState<any>({});
  const [localExerciseData, setLocalExerciseData] = useState<Record<string, any>>({});
  const [localExerciseOptionData, setLocalExerciseOptionData] = useState<Record<string, any>>({});

  // Fetch program data if editing
  const { data: fetchedProgramData, isLoading: isLoadingProgram } = useProgramByIdQuery(
    programData?.id || null
  );
  
  // Fetch programConstrained data only if we're editing an existing program
  // We don't need to check for constraints since we want to load all data for existing programs
  const { data: fetchedProgramConstrainedData, isLoading: isLoadingProgramConstrained } = useProgramConstrainedByIdQuery(
    programData?.id || null
  );
  
  console.log('fetchedProgramConstrainedData from query:', fetchedProgramConstrainedData);
  console.log('isLoadingProgramConstrained:', isLoadingProgramConstrained);

  // Reset all state when dialog closes or programData changes
  useEffect(() => {
    if (!open) {
      // Reset all state when dialog closes
      setIsPublished(false);
      setTreeData([]);
      setSelectedId(null);
      setIsSaving(false);
      setFormData({});
      setLocalStepData({});
      setLocalStepOptionData({});
      setLocalProgramData({});
      setExerciseTreeData([]);
      setSelectedExerciseId(null);
      setExerciseFormData({});
      setLocalExerciseData({});
      setLocalExerciseOptionData({});
    }
  }, [open, programData?.id]);

  // Initialize data when program is loaded or when creating new
  useEffect(() => {
    if (open) {
      if (fetchedProgramData || fetchedProgramConstrainedData) {
        // Editing existing program - load data from API
        console.log('Loading program data from API:', fetchedProgramData || fetchedProgramConstrainedData);
        
        // Use programConstrained data if available, otherwise use basic program data
        const dataToUse = fetchedProgramConstrainedData || fetchedProgramData;
        
        if (!dataToUse) {
          console.log('No data available for loading');
          return;
        }
        
        // Set published status
        setIsPublished(dataToUse.status === 'published');
        
        // Convert API data to tree structure (only if programConstrained data exists)
        // For base programs, use fetchedProgramData.programConstrained
        // For constrained programs, use fetchedProgramConstrainedData directly
        const programConstrainedData = fetchedProgramConstrainedData || fetchedProgramData?.programConstrained;
        
        if (programConstrainedData) {
          console.log('programConstrainedData exists:', programConstrainedData);
          console.log('programConstrainedData.steps:', programConstrainedData.steps);
          
          const steps = programConstrainedData.steps?.map((step, stepIndex) => ({
            id: `step-${stepIndex + 1}`,
            label: `Step ${stepIndex + 1}`,
                          children: step.options?.map((option, optionIndex) => ({
                id: option.id || `step-${stepIndex + 1}-option-${optionIndex + 1}`,
                label: option.name || option.displayName?.[0]?.text || `Step Option ${optionIndex + 1}`
              })) || []
          })) || [];
          
          console.log('Setting tree data:', steps);
          setTreeData(steps);
          
          // Initialize local data with fetched data
          const stepData: Record<string, any> = {};
          const stepOptionData: Record<string, any> = {};
          
          programConstrainedData.steps?.forEach((step, stepIndex) => {
            const stepId = `step-${stepIndex + 1}`;
            stepData[stepId] = {
              name: `Step ${stepIndex + 1}`, // Steps don't have names in API
              repetitions: 1, // Default value since steps don't have this field
              duration: 0, // Default value since steps don't have this field
              displayName: [{ language: 'en', text: `Step ${stepIndex + 1}` }] // Default display name
            };
            
            step.options?.forEach((option, optionIndex) => {
              const optionId = option.id || `${stepId}-option-${optionIndex + 1}`;
              const convertedConstraints = constraintsObjectToArray(option.constraints);
              console.log(`Step option ${optionId} constraints:`, option.constraints, 'converted to:', convertedConstraints);
              
              console.log(`Step option ${optionId} raw data:`, option);
              console.log(`Step option ${optionId} name:`, option.name);
              console.log(`Step option ${optionId} displayName:`, option.displayName);
              
              stepOptionData[optionId] = {
                stepOptionName: option.name, // Use the name field for generic name
                duration: option.duration,
                repetition: option.repetitions || 1, // Add missing repetition field
                constraints: convertedConstraints,
                displayName: option.displayName ? option.displayName.reduce((acc: any, item: any) => {
                  acc[item.language] = item.text;
                  return acc;
                }, {}) : {}, // Convert displayName array to object format
                exercises: option.exercises?.map((exercise, exerciseIndex) => ({
                  id: exercise.id || `${optionId}-exercise-${exerciseIndex + 1}`,
                  label: `Exercise ${exerciseIndex + 1}`,
                  children: exercise.options?.map((exerciseOption, exerciseOptionIndex) => {
                    const exerciseOptionId = exerciseOption.id || `${exercise.id || `${optionId}-exercise-${exerciseIndex + 1}`}-option-${exerciseOptionIndex + 1}`;
                    console.log(`Creating exercise option node with ID:`, exerciseOptionId);
                    return {
                      id: exerciseOptionId,
                      label: exerciseOption.exercise?.name || `Exercise Option ${exerciseOptionIndex + 1}`
                    };
                  }) || []
                })) || []
              };
              
              console.log(`Step option ${optionId} processed data:`, stepOptionData[optionId]);
              
              // Also populate local exercise data with fetched data
              const exerciseData: Record<string, any> = {};
              const exerciseOptionData: Record<string, any> = {};
              
              option.exercises?.forEach((exercise, exerciseIndex) => {
                const exerciseId = exercise.id || `${optionId}-exercise-${exerciseIndex + 1}`;
                
                // Store exercise data
                const exerciseConstraints = constraintsObjectToArray(exercise.constraints);
                console.log(`Exercise ${exerciseId} constraints:`, exercise.constraints, 'converted to:', exerciseConstraints);
                
                exerciseData[exerciseId] = {
                  name: `Exercise ${exerciseIndex + 1}`,
                  duration: exercise.duration,
                  recuperation: exercise.recuperation,
                  repetitions: exercise.repetitions,
                  series: exercise.series,
                  constraints: exerciseConstraints
                };
                
                // Store exercise option data
                exercise.options?.forEach((exerciseOption, exerciseOptionIndex) => {
                  const exerciseOptionId = exerciseOption.id || `${exerciseId}-option-${exerciseOptionIndex + 1}`;
                  const exerciseOptionConstraints = constraintsObjectToArray(exerciseOption.constraints);
                  console.log(`Exercise option ${exerciseOptionId} constraints:`, exerciseOption.constraints, 'converted to:', exerciseOptionConstraints);
                  
                  exerciseOptionData[exerciseOptionId] = {
                    exerciseId: exerciseOption.exercise?.id,
                    exerciseName: exerciseOption.exercise?.name,
                    duration: exerciseOption.duration,
                    recuperation: exerciseOption.recuperation,
                    repetitions: exerciseOption.repetitions,
                    series: exerciseOption.series,
                    constraints: exerciseOptionConstraints
                  };
                });
              });
              
              // Set the exercise data for this step option
              console.log('Setting exercise data:', exerciseData);
              console.log('Setting exercise option data:', exerciseOptionData);
              setLocalExerciseData(prev => ({ ...prev, ...exerciseData }));
              setLocalExerciseOptionData(prev => ({ ...prev, ...exerciseOptionData }));
            });
          });
          
          setLocalStepData(stepData);
          setLocalStepOptionData(stepOptionData);
        } else {
          // No programConstrained data, so no steps
          setTreeData([]);
          setLocalStepData({});
          setLocalStepOptionData({});
        }
        
        // Initialize form data with program data
        const programConstraints = constraintsObjectToArray(dataToUse.constraints);
        console.log('Program constraints:', dataToUse.constraints, 'converted to:', programConstraints);
        
        const programFormData = {
          name: dataToUse.name,
          status: dataToUse.status,
          description: dataToUse.description,
          displayName: dataToUse.displayName,
          poster: dataToUse.poster,
          constraints: programConstraints
        };
        
        setFormData(programFormData);
        setLocalProgramData(programFormData); // Also set local program data
        
      } else if (!isLoadingProgram && !isLoadingProgramConstrained) {
        // Creating new program - initialize empty state
        console.log('Initializing new program');
        setIsPublished(false);
        setTreeData([]);
        setFormData({});
        setLocalStepData({});
        setLocalStepOptionData({});
        setLocalProgramData({});
      }
    }
  }, [open, fetchedProgramData, fetchedProgramConstrainedData, isLoadingProgram, isLoadingProgramConstrained]);

  // Initialize status from program data when dialog opens
  useEffect(() => {
    // Only set isPublished from programData.status if creating a new program (no id)
    if (programData && open && !programData.id) {
      const initialStatus = programData.status === 'published';
      setIsPublished(initialStatus);
      console.log('Initializing status from program data (new program):', initialStatus);
    }
  }, [programData, open]);

  // Find the selected node
  const selectedNode = findNodeById(treeData, selectedId);

  // Handler to add a new step or step option (only two levels allowed)
  const handleAdd = (parentId?: string) => {
    try {
      if (!parentId) {
        setTreeData(prev => [
          ...prev,
          { id: `step-${prev.length + 1}`, label: `Step ${prev.length + 1}`, children: [] }
        ]);
        showSuccess('Step Added', 'New step has been added to the program.');
      } else {
        setTreeData(prev =>
          prev.map(node =>
            node.id === parentId
              ? {
                  ...node,
                  children: [
                    ...(node.children || []),
                    { id: `${parentId}-option-${(node.children?.length || 0) + 1}`, label: 'New Step Option' }
                  ]
                }
              : node
          )
        );
        showSuccess('Step Option Added', 'New step option has been added to the step.');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      showError('Add Failed', 'Failed to add item. Please try again.');
    }
  };

  // Handle node selection and restore form data
  const handleNodeSelect = (nodeId: string | null) => {
    console.log('handleNodeSelect called with:', nodeId);
    console.log('current selectedId:', selectedId);
    console.log('localProgramData:', localProgramData);
    
    if (selectedId !== nodeId) {
      // If we're currently at program level and have form data, save it before switching
      if (!selectedId && formData && Object.keys(formData).length > 0) {
        console.log('Saving current program data before switching:', formData);
        setLocalProgramData(formData);
      }
      
      if (nodeId === null) {
        // Going back to program level - restore program data
        console.log('Restoring program data:', localProgramData);
        setFormData(localProgramData);
        
        // Restore status from local data
        if (localProgramData.status) {
          const isPublishedStatus = localProgramData.status === 'published';
          console.log('Restoring status from local data - localProgramData.status:', localProgramData.status);
          console.log('Restoring status from local data - isPublishedStatus:', isPublishedStatus);
          console.log('Current isPublished state before restore:', isPublished);
          setIsPublished(isPublishedStatus);
          console.log('Restoring status from local data:', isPublishedStatus);
        } else {
          console.log('No status found in localProgramData, keeping current isPublished state:', isPublished);
        }
      } else {
        // Going to a step/step option - restore saved data if available
        const targetNode = findNodeById(treeData, nodeId);
        if (targetNode) {
          if (targetNode.children) {
            // Going to a step - restore step data
            const stepData = localStepData[nodeId];
            console.log('Restoring step data:', stepData);
            setFormData(stepData || {});
          } else {
            // Going to a step option - restore step option data
            const stepOptionData = localStepOptionData[nodeId];
            console.log('Restoring step option data for nodeId:', nodeId);
            console.log('Available step option data keys:', Object.keys(localStepOptionData));
            console.log('Step option data:', stepOptionData);
            console.log('Step option stepOptionName:', stepOptionData?.stepOptionName);
            console.log('Step option displayName:', stepOptionData?.displayName);
            setFormData(stepOptionData || {});
          }
        } else {
          console.log('Clearing form data - no saved data found');
          setFormData({});
        }
      }
    }
    setSelectedId(nodeId);
  };

  // Build breadcrumbs based on current selection
  const buildBreadcrumbs = () => {
    const breadcrumbs = [];
    
    // Always start with program name (clickable) - use local data if available
    const programName = localProgramData.name || formData.name || 'New Program';
    breadcrumbs.push({
      label: programName,
      isClickable: true,
      isCurrent: !selectedNode // If no selected node, we're at program level
    });

    if (!selectedNode) {
      return breadcrumbs;
    }

    // If we're on a step
    if (selectedNode && !selectedNode.children) {
      // Find the parent step
      const parentStep = treeData.find(step => 
        step.children?.some(child => child.id === selectedNode.id)
      );
      
      if (parentStep) {
        // Add step name (info only) - use local data if available
        const stepLocalData = localStepData[parentStep.id];
        const stepLabel = stepLocalData?.name || parentStep.label;
        
        breadcrumbs.push({
          label: stepLabel,
          isClickable: false,
          isCurrent: false
        });
        
        // Add step option name (current, clickable) - use local data if available
        const optionLocalData = localStepOptionData[selectedNode.id];
        const optionLabel = optionLocalData?.stepOptionName || selectedNode.label;
        
        breadcrumbs.push({
          label: optionLabel,
          isClickable: true,
          isCurrent: true
        });
        
        // If we have a selected exercise, add exercise breadcrumbs
        if (selectedExerciseId) {
          const selectedExerciseNode = findNodeById(exerciseTreeData, selectedExerciseId);
          if (selectedExerciseNode) {
            if (selectedExerciseNode.children) {
              // We're on an exercise level
              const exerciseName = localExerciseData[selectedExerciseNode.id]?.name || selectedExerciseNode.label;
              breadcrumbs.push({
                label: exerciseName,
                isClickable: false,
                isCurrent: false
              });
            } else {
              // We're on an exercise option level
              const exerciseParent = exerciseTreeData.find(exercise => 
                exercise.children?.some(child => child.id === selectedExerciseId)
              );
              
              if (exerciseParent) {
                const exerciseName = localExerciseData[exerciseParent.id]?.name || exerciseParent.label;
                breadcrumbs.push({
                  label: exerciseName,
                  isClickable: false,
                  isCurrent: false
                });
                
                const exerciseOptionName = localExerciseOptionData[selectedExerciseId]?.exerciseName || selectedExerciseNode.label;
                breadcrumbs.push({
                  label: exerciseOptionName,
                  isClickable: true,
                  isCurrent: true
                });
              }
            }
          }
        }
      }
    } else if (selectedNode && selectedNode.children) {
      // We're on a step level - use local data if available
      const stepLocalData = localStepData[selectedNode.id];
      const stepLabel = stepLocalData?.name || selectedNode.label;
      
      breadcrumbs.push({
        label: stepLabel,
        isClickable: true,
        isCurrent: true
      });
    }

    return breadcrumbs;
  };

  // Handle back navigation
  const handleBack = () => {
    if (exerciseTreeData.length > 0) {
      // We're in exercise management, go back to step option level
      // Save the current exercise tree data to step option's local data before clearing
      if (selectedNode) {
        setLocalStepOptionData(prev => ({
          ...prev,
          [selectedNode.id]: {
            ...prev[selectedNode.id],
            exercises: exerciseTreeData
          }
        }));
        console.log('Saved exercise tree data before back navigation:', exerciseTreeData);
      }
      setSelectedExerciseId(null);
      setExerciseFormData({});
      // Exit exercise management mode by clearing exercise tree data
      setExerciseTreeData([]);
    } else if (selectedNode && !selectedNode.children) {
      // We're on a step option, go back to program level
      handleNodeSelect(null);
    } else if (selectedNode && selectedNode.children) {
      // We're on a step, go back to program level
      handleNodeSelect(null);
    }
  };

  // Handle breadcrumb click
  const handleBreadcrumbClick = (idx: number) => {
    // If we're currently at program level and have form data, save it before switching
    if (!selectedId && formData && Object.keys(formData).length > 0) {
      console.log('Saving current program data before breadcrumb navigation:', formData);
      setLocalProgramData(formData);
    }
    
    if (idx === 0) {
      // Program level - clear all exercise state
      setExerciseTreeData([]);
      setSelectedExerciseId(null);
      setExerciseFormData({});
      handleNodeSelect(null);
    } else if (idx === 2) {
      // Step option level - exit exercise management mode
      // Save current exercise tree data before clearing
      if (selectedNode && exerciseTreeData.length > 0) {
        setLocalStepOptionData(prev => ({
          ...prev,
          [selectedNode.id]: {
            ...prev[selectedNode.id],
            exercises: exerciseTreeData
          }
        }));
        console.log('Saved exercise tree data before breadcrumb navigation:', exerciseTreeData);
      }
      setExerciseTreeData([]);
      setSelectedExerciseId(null);
      setExerciseFormData({});
    } else if (idx === 3) {
      // Exercise level - clear exercise option selection
      setSelectedExerciseId(null);
      setExerciseFormData({});
    }
  };

  // Handler for program auto-save on blur
  const handleProgramBlur = (programData: any) => {
    setLocalProgramData(programData);
    console.log('Program data saved locally:', programData);
  };

  // Handler for status change - save locally
  const handleStatusChange = (newIsPublished: boolean) => {
    console.log('handleStatusChange called with:', newIsPublished);
    console.log('Current isPublished state:', isPublished);
    console.log('Current localProgramData before update:', localProgramData);
    setIsPublished(newIsPublished);
    setLocalProgramData((prev: any) => {
      const updated = {
        ...prev,
        status: newIsPublished ? 'published' : 'draft'
      };
      console.log('Updated localProgramData:', updated);
      return updated;
    });
    console.log('Status saved locally:', newIsPublished ? 'published' : 'draft');
  };

  // Handler for removing steps and step options
  const handleRemove = (nodeId: string) => {
    console.log('Removing node:', nodeId);
    
    try {
      // Check if it's a step or step option
      const isStep = treeData.some(step => step.id === nodeId);
      
      if (isStep) {
        // Remove step and all its children
        setTreeData(prev => prev.filter(step => step.id !== nodeId));
        
        // Clear local data for this step and its children
        const stepToRemove = treeData.find(step => step.id === nodeId);
        if (stepToRemove?.children) {
          setLocalStepOptionData(prev => {
            const newData = { ...prev };
            stepToRemove.children?.forEach(child => {
              delete newData[child.id];
            });
            return newData;
          });
        }
        setLocalStepData(prev => {
          const newData = { ...prev };
          delete newData[nodeId];
          return newData;
        });
        
        console.log('Step removed:', nodeId);
        showSuccess('Step Removed', 'Step and all its options have been removed.');
      } else {
        // Remove step option
        setTreeData(prev => prev.map(step => ({
          ...step,
          children: step.children?.filter(child => child.id !== nodeId)
        })));
        
        // Clear local data for this step option
        setLocalStepOptionData(prev => {
          const newData = { ...prev };
          delete newData[nodeId];
          return newData;
        });
        
        console.log('Step option removed:', nodeId);
        showSuccess('Step Option Removed', 'Step option has been removed.');
      }
      
      // If the removed node was selected, go back to program level
      if (selectedId === nodeId) {
        handleNodeSelect(null);
      }
    } catch (error) {
      console.error('Error removing node:', error);
      showError('Remove Failed', 'Failed to remove item. Please try again.');
    }
  };

  // Handler for local saving of step options
  const handleLocalSave = (nodeData: any) => {
    if (selectedNode) {
      try {
        if (selectedNode.children) {
          // We're on a step level
          setLocalStepData(prev => ({
            ...prev,
            [selectedNode.id]: nodeData
          }));
          
          // Update tree data with the new name
          setTreeData(prev => prev.map(step => 
            step.id === selectedNode.id 
              ? { ...step, label: nodeData.name || step.label }
              : step
          ));
          
          console.log('Step data saved locally:', nodeData);
          showSuccess('Step Saved', 'Step data has been saved locally.');
        } else {
          // We're on a step option level
          console.log('Saving step option data:', nodeData);
          
          setLocalStepOptionData(prev => {
            console.log('Current step option data before save:', prev[selectedNode.id]);
            const currentExercises = prev[selectedNode.id]?.exercises || [];
            console.log('Current exercises to preserve:', currentExercises);
            
            const updatedData = {
              ...prev,
              [selectedNode.id]: {
                ...prev[selectedNode.id], // Preserve existing data including exercises
                ...nodeData, // Override with new form data
                exercises: currentExercises // Explicitly preserve exercises
              }
            };
            console.log('Updated step option data:', updatedData[selectedNode.id]);
            return updatedData;
          });
          
          // Update tree data with the new name
          setTreeData(prev => prev.map(step => ({
            ...step,
            children: step.children?.map(child => 
              child.id === selectedNode.id 
                ? { ...child, label: nodeData.stepOptionName || child.label }
                : child
            )
          })));
          
          console.log('Step option data saved locally:', nodeData);
          showSuccess('Step Option Saved', 'Step option data has been saved locally.');
        }
      } catch (error) {
        console.error('Error saving locally:', error);
        showError('Save Failed', 'Failed to save data locally. Please try again.');
      }
    }
  };

  // Exercise handlers
  const handleExerciseAdd = (parentId?: string) => {
    try {
      let updatedExerciseTreeData: TreeNode[];
      
      if (!parentId) {
        // Add new exercise
        updatedExerciseTreeData = [
          ...exerciseTreeData,
          { id: `exercise-${exerciseTreeData.length + 1}`, label: `Exercise ${exerciseTreeData.length + 1}`, children: [] }
        ];
        setExerciseTreeData(updatedExerciseTreeData);
        showSuccess('Exercise Added', 'New exercise has been added to the step option.');
      } else {
        // Add new exercise option
        updatedExerciseTreeData = exerciseTreeData.map(node =>
          node.id === parentId
            ? {
                ...node,
                children: [
                  ...(node.children || []),
                  { id: `${parentId}-option-${(node.children?.length || 0) + 1}`, label: 'New Exercise Option' }
                ]
              }
            : node
        );
        setExerciseTreeData(updatedExerciseTreeData);
        showSuccess('Exercise Option Added', 'New exercise option has been added to the exercise.');
      }
      
      // Save the updated exercise tree data to the step option's local data
      if (selectedNode) {
        setLocalStepOptionData(prev => ({
          ...prev,
          [selectedNode.id]: {
            ...prev[selectedNode.id],
            exercises: updatedExerciseTreeData
          }
        }));
        console.log('Saved exercise tree data after add:', updatedExerciseTreeData);
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      showError('Add Failed', 'Failed to add exercise. Please try again.');
    }
  };

  const handleExerciseRemove = (nodeId: string) => {
    console.log('Removing exercise node:', nodeId);
    
    try {
      // Check if it's an exercise or exercise option
      const isExercise = exerciseTreeData.some(exercise => exercise.id === nodeId);
      let updatedExerciseTreeData: TreeNode[];
      
      if (isExercise) {
        // Remove exercise and all its children
        updatedExerciseTreeData = exerciseTreeData.filter(exercise => exercise.id !== nodeId);
        setExerciseTreeData(updatedExerciseTreeData);
        
        // Clear local data for this exercise and its children
        const exerciseToRemove = exerciseTreeData.find(exercise => exercise.id === nodeId);
        if (exerciseToRemove?.children) {
          setLocalExerciseOptionData(prev => {
            const newData = { ...prev };
            exerciseToRemove.children?.forEach(child => {
              delete newData[child.id];
            });
            return newData;
          });
        }
        setLocalExerciseData(prev => {
          const newData = { ...prev };
          delete newData[nodeId];
          return newData;
        });
        
        console.log('Exercise removed:', nodeId);
        showSuccess('Exercise Removed', 'Exercise and all its options have been removed.');
      } else {
        // Remove exercise option
        updatedExerciseTreeData = exerciseTreeData.map(exercise => ({
          ...exercise,
          children: exercise.children?.filter(child => child.id !== nodeId)
        }));
        setExerciseTreeData(updatedExerciseTreeData);
        
        // Clear local data for this exercise option
        setLocalExerciseOptionData(prev => {
          const newData = { ...prev };
          delete newData[nodeId];
          return newData;
        });
        
        console.log('Exercise option removed:', nodeId);
        showSuccess('Exercise Option Removed', 'Exercise option has been removed.');
      }
      
      // If the removed node was selected, go back to exercise level
      if (selectedExerciseId === nodeId) {
        handleExerciseSelect(null);
      }
      
      // Save the updated exercise tree data to the step option's local data
      if (selectedNode) {
        setLocalStepOptionData(prev => ({
          ...prev,
          [selectedNode.id]: {
            ...prev[selectedNode.id],
            exercises: updatedExerciseTreeData
          }
        }));
        console.log('Saved exercise tree data after remove:', updatedExerciseTreeData);
      }
    } catch (error) {
      console.error('Error removing exercise:', error);
      showError('Remove Failed', 'Failed to remove exercise. Please try again.');
    }
  };

  const handleExerciseSelect = (nodeId: string | null) => {
    console.log('handleExerciseSelect called with:', nodeId);
    console.log('localExerciseOptionData keys:', Object.keys(localExerciseOptionData));
    console.log('localExerciseData keys:', Object.keys(localExerciseData));
    
    if (selectedExerciseId !== nodeId) {
      if (nodeId === null) {
        // Going back to exercise level - clear form data
        setExerciseFormData({});
      } else {
        // Going to an exercise/exercise option - restore saved data if available
        const targetNode = findNodeById(exerciseTreeData, nodeId);
        if (targetNode) {
          if (targetNode.children) {
            // Going to an exercise - restore exercise data
            const exerciseData = localExerciseData[nodeId];
            console.log('Restoring exercise data:', exerciseData);
            setExerciseFormData(exerciseData || {});
          } else {
            // Going to an exercise option - restore exercise option data
            // The exercise option data is stored with the same ID as the node
            const exerciseOptionData = localExerciseOptionData[nodeId];
            console.log('Restoring exercise option data for nodeId:', nodeId, 'data:', exerciseOptionData);
            console.log('All localExerciseOptionData keys:', Object.keys(localExerciseOptionData));
            console.log('Exercise option constraints:', exerciseOptionData?.constraints);
            setExerciseFormData(exerciseOptionData || {});
          }
        } else {
          console.log('Clearing exercise form data - no saved data found');
          setExerciseFormData({});
        }
      }
    }
    setSelectedExerciseId(nodeId);
  };

  const handleExerciseLocalSave = async (nodeData: any) => {
    if (selectedExerciseId) {
      try {
        const targetNode = findNodeById(exerciseTreeData, selectedExerciseId);
        if (targetNode) {
          if (targetNode.children) {
            // We're on an exercise level
            setLocalExerciseData(prev => ({
              ...prev,
              [selectedExerciseId]: nodeData
            }));
            
            // Update tree data with the new name
            setExerciseTreeData(prev => prev.map(exercise => 
              exercise.id === selectedExerciseId 
                ? { ...exercise, label: nodeData.name || exercise.label }
                : exercise
            ));
            
            console.log('Exercise data saved locally:', nodeData);
            showSuccess('Exercise Saved', 'Exercise data has been saved locally.');
          } else {
            // We're on an exercise option level - handle file uploads first
            let processedData = { ...nodeData };
            
            // Handle file uploads if needed
            if (nodeData.videoFile && nodeData.videoType === 'upload') {
              const uploadResult = await uploadVideoMultipart(nodeData.videoFile, showError);

              if (!uploadResult) {
                showError(`Failed to upload background video`);
                return;
              }

              const { publicUrl, posterUrl } = uploadResult;

              if (publicUrl) {
                processedData.backgroundVideo = {
                  ...processedData.backgroundVideo,
                  source: publicUrl
                };
              }
              // Remove the file from the data
              delete processedData.videoFile;
            }
            
            if (nodeData.posterFile && nodeData.posterType === 'upload') {
              const uploadedPosterUrl = await uploadMedia(nodeData.posterFile, 'image');
              if (uploadedPosterUrl) {
                processedData.poster = {
                  ...processedData.poster,
                  source: uploadedPosterUrl
                };
              }
              // Remove the file from the data
              delete processedData.posterFile;
            }
            
            setLocalExerciseOptionData(prev => ({
              ...prev,
              [selectedExerciseId]: processedData
            }));
            
            // Update tree data with the new name
            console.log('Updating tree with exercise name:', processedData.exerciseName);
            const updatedExerciseTreeData = exerciseTreeData.map(exercise => ({
              ...exercise,
              children: exercise.children?.map(child => 
                child.id === selectedExerciseId 
                  ? { ...child, label: processedData.exerciseName || child.label }
                  : child
              )
            }));
            console.log('Updated exercise tree data:', updatedExerciseTreeData);
            
            // Update the exercise tree data state
            setExerciseTreeData(updatedExerciseTreeData);
            
            // Also save the updated exercise tree data to the step option's local data
            if (selectedNode) {
              setLocalStepOptionData(prev => ({
                ...prev,
                [selectedNode.id]: {
                  ...prev[selectedNode.id],
                  exercises: updatedExerciseTreeData
                }
              }));
            }
            
            console.log('Exercise option data saved locally:', processedData);
            console.log('Exercise option exerciseId:', processedData.exerciseId);
            console.log('Exercise option exerciseName:', processedData.exerciseName);
            showSuccess('Exercise Option Saved', 'Exercise option data has been saved locally.');
          }
        }
      } catch (error) {
        console.error('Error saving exercise locally:', error);
        showError('Save Failed', 'Failed to save exercise data locally. Please try again.');
      }
    }
  };

  // Handler for saving the entire program
  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // First, apply any pending local changes from the current form
      let updatedLocalStepData = { ...localStepData };
      let updatedLocalStepOptionData = { ...localStepOptionData };
      let updatedLocalProgramData = { ...localProgramData };
      let updatedLocalExerciseData = { ...localExerciseData };
      let updatedLocalExerciseOptionData = { ...localExerciseOptionData };
      
      if (selectedNode) {
        console.log('Applying pending local changes for selected node:', selectedNode.id);
        
        if (selectedNode.children) {
          // We're on a step level - apply current form data
          updatedLocalStepData = {
            ...updatedLocalStepData,
            [selectedNode.id]: { ...updatedLocalStepData[selectedNode.id], ...formData }
          };
        } else {
          // We're on a step option level - apply current form data
          updatedLocalStepOptionData = {
            ...updatedLocalStepOptionData,
            [selectedNode.id]: { ...updatedLocalStepOptionData[selectedNode.id], ...formData }
          };
        }
      } else {
        // We're on the program level - apply current form data to program
        updatedLocalProgramData = { ...updatedLocalProgramData, ...formData };
      }
      
      // If we're in exercise management mode, also apply exercise form data
      if (selectedNode && !selectedNode.children && exerciseTreeData.length > 0 && selectedExerciseId) {
        console.log('Applying pending exercise changes for:', selectedExerciseId);
        updatedLocalExerciseData = {
          ...updatedLocalExerciseData,
          [selectedExerciseId]: { ...updatedLocalExerciseData[selectedExerciseId], ...exerciseFormData }
        };
        
        // Also update the exercise data in the step option's exercises array
        const stepOptionId = selectedNode.id;
        if (updatedLocalStepOptionData[stepOptionId]) {
          const updatedExercises = updatedLocalStepOptionData[stepOptionId].exercises?.map((exercise: any) => {
            if (exercise.id === selectedExerciseId) {
              return {
                ...exercise,
                ...exerciseFormData
              };
            }
            return exercise;
          }) || [];
          
          updatedLocalStepOptionData[stepOptionId] = {
            ...updatedLocalStepOptionData[stepOptionId],
            exercises: updatedExercises
          };
          
          // Also update the exercise option data for the selected exercise
          // selectedExerciseId is actually the exercise option ID, not the exercise ID
          // We need to find the exercise that contains this exercise option
          const exercise = updatedExercises.find((ex: any) => 
            ex.children && ex.children.some((child: any) => child.id === selectedExerciseId)
          );
          console.log('Found exercise for selectedExerciseId:', exercise);
          if (exercise) {
            // Update the exercise option data directly using selectedExerciseId
            console.log('Updating exercise option data for exerciseOptionId:', selectedExerciseId);
            console.log('exerciseFormData:', exerciseFormData);
            console.log('Previous exercise option data:', updatedLocalExerciseOptionData[selectedExerciseId]);
            updatedLocalExerciseOptionData = {
              ...updatedLocalExerciseOptionData,
              [selectedExerciseId]: { ...updatedLocalExerciseOptionData[selectedExerciseId], ...exerciseFormData }
            };
            console.log('Updated exercise option data:', updatedLocalExerciseOptionData[selectedExerciseId]);
          }
        }
      }
      
      // Always save as programConstrained since the schema only supports constrained programs
      console.log('Building mutation payload with treeData:', treeData);
      console.log('updatedLocalStepData:', updatedLocalStepData);
      console.log('updatedLocalStepOptionData:', updatedLocalStepOptionData);
      
      // Handle file uploads for program poster if needed
      let processedFormData = { ...formData };
      
      // Check if there's a poster file to upload
      console.log('Checking for poster upload - formData.posterFile:', formData.posterFile);
      console.log('Checking for poster upload - formData.posterType:', formData.posterType);
      console.log('Checking for poster upload - localProgramData.posterFile:', localProgramData.posterFile);
      console.log('Checking for poster upload - localProgramData.posterType:', localProgramData.posterType);
      
      // Check for poster file in formData (main program level) or localProgramData (other levels)
      const posterFile = formData.posterFile || localProgramData.posterFile;
      const posterType = formData.posterType || localProgramData.posterType;
      
      if (posterFile && posterType === 'upload') {
        console.log('Uploading program poster file:', posterFile);
        const uploadedPosterUrl = await uploadMedia(posterFile, 'image');
        console.log('Upload result URL:', uploadedPosterUrl);
        if (uploadedPosterUrl) {
          processedFormData.poster = {
            source: uploadedPosterUrl
          };
          console.log('Program poster uploaded successfully:', uploadedPosterUrl);
        } else {
          console.error('Failed to upload program poster');
          showError('Upload Failed', 'Failed to upload program poster. Please try again.');
          setIsSaving(false);
          return;
        }
        // Remove the file from the data
        delete processedFormData.posterFile;
      } else {
        console.log('No poster file to upload or posterType is not upload');
        // Check if there's an existing poster URL that needs to be preserved
        if (formData.poster && formData.poster.source) {
          console.log('Preserving existing poster URL:', formData.poster.source);
          processedFormData.poster = formData.poster;
        }
      }
      
      // Helper function to convert constraint array format to object format
      const convertConstraintsToObject = (constraints: any[]) => {
        if (!constraints || !Array.isArray(constraints)) return {};
        
        const constraintObject: any = {};
        constraints.forEach((constraint: any) => {
          if (constraint.type && constraint.values) {
            // Map constraint types to schema field names
            const fieldMap: Record<string, string> = {
              'Gender': 'gender',
              'Pathology': 'pathology',
              'Sport': 'sport',
              'Special program': 'specialProgram',
              'Week focus': 'weekFocus',
              'User level': 'userLevel',
              'Category': 'category',
              'Material': 'material'
            };
            
            const fieldName = fieldMap[constraint.type];
            if (fieldName) {
              constraintObject[fieldName] = constraint.values;
            }
          }
        });
        return constraintObject;
      };
      
      // Only include main program fields, exclude step-specific fields
      const mainProgramFields: any = {
        name: formData.name || updatedLocalProgramData.name,
        description: formData.description || updatedLocalProgramData.description,
        poster: formData.poster || updatedLocalProgramData.poster,
        posterType: formData.posterType || updatedLocalProgramData.posterType,
        posterFile: formData.posterFile || updatedLocalProgramData.posterFile
      };
      
      // Only include displayName in main program fields if we're NOT on a step level
      if (!selectedNode) {
        mainProgramFields.displayName = formData.displayName || updatedLocalProgramData.displayName;
      }
      
      console.log('selectedNode:', selectedNode);
      console.log('mainProgramFields:', mainProgramFields);
      console.log('updatedLocalProgramData.displayName:', updatedLocalProgramData.displayName);
      console.log('formData.displayName:', formData.displayName);
      
      // Filter out step-specific fields from formData to prevent contamination
      // Only include main program fields, exclude step-specific fields
      const stepSpecificFields = ['stepOptionName', 'duration', 'repetition', 'constraints', 'exercises'];
      const mainProgramFormData: any = {};
      
      // Only copy fields that are NOT step-specific
      // If we're on a step level, also exclude displayName to prevent contamination
      const fieldsToExclude = selectedNode ? [...stepSpecificFields, 'displayName'] : stepSpecificFields;
      
      Object.keys(formData).forEach(key => {
        if (!fieldsToExclude.includes(key)) {
          mainProgramFormData[key] = formData[key];
        }
      });
      
      console.log('formData keys:', Object.keys(formData));
      console.log('mainProgramFormData keys:', Object.keys(mainProgramFormData));
      console.log('mainProgramFormData.displayName:', mainProgramFormData.displayName);
      
      const mergedProgramData = {
        ...updatedLocalProgramData,
        ...mainProgramFields,
        ...mainProgramFormData,  // Use filtered form data to avoid step contamination
        ...processedFormData  // processedFormData contains uploaded file URLs
      };
      
      // If we're on a step level, ensure displayName is not included in main program data
      if (selectedNode) {
        // When on step level, preserve the original program's displayName instead of deleting it
        mergedProgramData.displayName = updatedLocalProgramData.displayName || programData?.displayName || [];
        console.log('Preserving original program displayName:', mergedProgramData.displayName);
      } else {
        console.log('Keeping program displayName in main program data');
      }
      
      console.log('mergedProgramData:', mergedProgramData);
      
      const mutationPayload = buildProgramConstrainedMutationPayload({
        ...mergedProgramData,
        status: isPublished ? 'published' : 'draft',
        // Ensure displayName and description are arrays of { language, text }
        displayName: mergedProgramData.displayName
          ? Array.isArray(mergedProgramData.displayName)
            ? mergedProgramData.displayName.map((d: any) =>
                d.language && d.text
                  ? d
                  : { language: Object.keys(d)[0], text: Object.values(d)[0] }
              )
            : Object.entries(mergedProgramData.displayName).map(([lang, text]) => ({ language: lang, text }))
          : [],
        description: mergedProgramData.description
          ? Array.isArray(mergedProgramData.description)
            ? mergedProgramData.description.map((d: any) =>
                d.language && d.text
                  ? d
                  : { language: Object.keys(d)[0], text: Object.values(d)[0] }
              )
            : Object.entries(mergedProgramData.description).map(([lang, text]) => ({ language: lang, text }))
          : [],
        steps: treeData.map(step => {
          const stepData = updatedLocalStepData[step.id] || {};
          console.log(`Processing step ${step.id}:`, step);
          console.log(`Step data for ${step.id}:`, stepData);
          
          return {
            options: step.children?.map(child => {
              const stepOptionData = updatedLocalStepOptionData[child.id] || {};
              console.log(`Processing step option ${child.id}:`, child);
              console.log(`Step option data for ${child.id}:`, stepOptionData);
              console.log(`Step option data keys:`, Object.keys(stepOptionData));
              console.log(`Step option exercises:`, stepOptionData.exercises);
              
              return {
                constraints: convertConstraintsToObject(stepOptionData.constraints || []),
                name: stepOptionData.stepOptionName || child.label,
                displayName: stepOptionData.displayName
                  ? Array.isArray(stepOptionData.displayName)
                    ? stepOptionData.displayName.map((d: any) =>
                        d.language && d.text
                          ? d
                          : { language: Object.keys(d)[0], text: Object.values(d)[0] }
                      )
                    : Object.entries(stepOptionData.displayName).map(([lang, text]) => ({ language: lang, text }))
                  : [],
                duration: parseInt(stepOptionData.duration) || 30,
                repetitions: parseInt(stepOptionData.repetition) || 1,
                exercises: stepOptionData.exercises?.map((exercise: any) => {
                  const exerciseData = updatedLocalExerciseData[exercise.id] || {};
                  console.log(`Building exercise for ${exercise.id}:`, exercise);
                  console.log(`Exercise data for ${exercise.id}:`, exerciseData);
                  
                  // Find all exercise options for this exercise
                  const exerciseOptions: any[] = [];
                  if (exercise.children) {
                    exercise.children.forEach((exerciseOption: any) => {
                      const exerciseOptionData = updatedLocalExerciseOptionData[exerciseOption.id] || {};
                      console.log(`Exercise option data for ${exerciseOption.id}:`, exerciseOptionData);
                      console.log(`Exercise option exerciseId:`, exerciseOptionData.exerciseId);
                      console.log(`Exercise option repetitions:`, exerciseOptionData.repetitions);
                      console.log(`Exercise option series:`, exerciseOptionData.series);
                      console.log(`Exercise option duration:`, exerciseOptionData.duration);
                      console.log(`Exercise option recuperation:`, exerciseOptionData.recuperation);
                      
                      // Include exercise options even if they don't have an exerciseId
                      // This allows for partial saves and better user experience
                      if (exerciseOptionData.exerciseId) {
                        exerciseOptions.push({
                          constraints: convertConstraintsToObject(exerciseOptionData.constraints || []),
                          repetitions: parseInt(exerciseOptionData.repetitions) || parseInt(exerciseData.repetitions) || 1,
                          series: parseInt(exerciseOptionData.series) || parseInt(exerciseData.series) || 1,
                          duration: parseInt(exerciseOptionData.duration) || parseInt(exerciseData.duration) || 30,
                          recuperation: parseInt(exerciseOptionData.recuperation) || parseInt(exerciseData.recuperation) || 0,
                          exerciseId: exerciseOptionData.exerciseId
                        });
                      } else {
                        // Include exercise options without exerciseId but with a warning
                        console.warn(`Exercise option ${exerciseOption.id} has no exerciseId, including with default values`);
                        exerciseOptions.push({
                          constraints: convertConstraintsToObject(exerciseOptionData.constraints || []),
                          repetitions: parseInt(exerciseOptionData.repetitions) || parseInt(exerciseData.repetitions) || 1,
                          series: parseInt(exerciseOptionData.series) || parseInt(exerciseData.series) || 1,
                          duration: parseInt(exerciseOptionData.duration) || parseInt(exerciseData.duration) || 30,
                          recuperation: parseInt(exerciseOptionData.recuperation) || parseInt(exerciseData.recuperation) || 0,
                          exerciseId: null // This will be handled by the backend or require user to select an exercise
                        });
                      }
                    });
                  }
                  
                  const exercisePayload = {
                    options: exerciseOptions
                  };
                  
                  console.log(`Exercise payload for ${exercise.id}:`, exercisePayload);
                  return exercisePayload;
                }) || []
              };
            }) || []
          };
        })
      });
      
      console.log('Saving programConstrained with payload:', JSON.stringify(mutationPayload, null, 2));
      console.log('Local exercise data keys:', Object.keys(localExerciseData));
      console.log('Local exercise option data keys:', Object.keys(localExerciseOptionData));
      console.log('Local exercise option data:', localExerciseOptionData);
      
      // Determine if this is create or update
      const isUpdate = programData?.id;
      const mutation = isUpdate ? updateProgramConstrainedMutation : createProgramConstrainedMutation;
      const variables = isUpdate 
        ? { id: programData.id, programConstrained: mutationPayload }
        : { programConstrained: mutationPayload };
      
      // Debug log for variables
      console.log('GraphQL mutation variables:', JSON.stringify(variables, null, 2));
      
      // Call the GraphQL mutation
      const response = await graphqlRequest(mutation, variables, showError);
      
      if (response?.createProgramConstrained?.id || response?.updateProgramConstrained?.id) {
        const action = isUpdate ? 'updated' : 'created';
        showSuccess(`Program ${action}`, `The program has been successfully ${action}.`);
        
        // Invalidate the programs queries to refresh the lists
        queryClient.invalidateQueries({ queryKey: ['programs'] });
        queryClient.invalidateQueries({ queryKey: ['constrainedPrograms'] });
        
        // Do NOT close the dialog here
      } else {
        const action = isUpdate ? 'updating' : 'creating';
        showError(`${action.charAt(0).toUpperCase() + action.slice(1)} Failed`, `Failed to ${action} program. Please check your input and try again.`);
      }
    } catch (error) {
      console.error('Error saving program:', error);
      const action = programData?.id ? 'updating' : 'creating';
      showError('Network Error', `An error occurred while ${action} the program. Please check your connection and try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} className="default-dialog">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
              width="100vw"
              height="100vh" 
              maxWidth="100vw" 
              maxHeight="100vh" 
              p='32px 48px'
              display="flex" 
              flexDirection="column"
              borderRadius="0"
              overflow="hidden"
              margin="0"
            >
            <ProgramTopBar
              breadcrumbs={buildBreadcrumbs()}
              selectedNode={selectedNode}
              onBreadcrumbClick={handleBreadcrumbClick}
              onBack={handleBack}
              isPublished={isPublished}
              setIsPublished={handleStatusChange}
              onCancel={() => onOpenChange(false)}
              onSave={selectedNode ? (!!(selectedNode && !selectedNode.children && exerciseTreeData.length > 0) ? () => handleExerciseLocalSave(exerciseFormData) : handleLocalSave) : handleSave}
              formData={!!(selectedNode && !selectedNode.children && exerciseTreeData.length > 0) ? exerciseFormData : formData}
              isExerciseManagement={!!(selectedNode && !selectedNode.children && exerciseTreeData.length > 0)}
              onGeneralSave={handleSave}
              isSaving={isSaving}
            />
            <Flex flex="1" minH={0}>
              {selectedNode && !selectedNode.children && exerciseTreeData.length > 0 ? (
                // Step Option selected and exercises exist - show exercise management
                <Flex flex={1}>
                  <ExerciseTreePanel
                    exerciseData={exerciseTreeData}
                    selectedId={selectedExerciseId}
                    onSelect={handleExerciseSelect}
                    onAdd={handleExerciseAdd}
                    onRemove={handleExerciseRemove}
                  />
                  <ExerciseFormPanel
                    selectedNode={selectedExerciseId ? findNodeById(exerciseTreeData, selectedExerciseId) : null}
                    formData={exerciseFormData}
                    onChange={setExerciseFormData}
                    onBlur={handleExerciseLocalSave}
                  />
                </Flex>
              ) : (
                // Program, Step, or Step Option (without exercises) - show normal layout
                <>
                  <ProgramTreePanel
                    treeData={treeData}
                    selectedId={selectedId}
                    onSelect={handleNodeSelect}
                    onAdd={handleAdd}
                    onRemove={handleRemove}
                  />
                  <ProgramFormPanel
                    selectedNode={selectedNode}
                    formData={formData}
                    onChange={setFormData}
                    onBlur={!selectedNode ? handleProgramBlur : undefined}
                    isPublished={isPublished}
                    isEditing={!!programData?.id}
                    onAddExercise={() => {
                      console.log('Add Exercise button clicked!');
                      console.log('selectedNode:', selectedNode);
                      console.log('selectedNode.children:', selectedNode?.children);
                      console.log('selectedNode.id:', selectedNode?.id);
                      
                      // Initialize exercise tree for this step option
                      if (selectedNode && !selectedNode.children) {
                        console.log('Condition met: selectedNode exists and has no children');
                        // Check if we have saved exercise data for this step option
                        const savedStepOptionData = localStepOptionData[selectedNode.id];
                        console.log('savedStepOptionData:', savedStepOptionData);
                        console.log('savedStepOptionData.exercises:', savedStepOptionData?.exercises);
                        
                        if (savedStepOptionData && savedStepOptionData.exercises && savedStepOptionData.exercises.length > 0) {
                          // Restore saved exercise tree
                          console.log('Restoring saved exercise data:', savedStepOptionData.exercises);
                          setExerciseTreeData(savedStepOptionData.exercises);
                          setSelectedExerciseId(null);
                          setExerciseFormData({});
                        } else {
                          // Add a default exercise to start with
                          console.log('No saved exercise data or empty exercises array, creating default');
                          const newExercise = { id: `exercise-1`, label: 'Exercise 1', children: [] };
                          setExerciseTreeData([newExercise]);
                          setSelectedExerciseId(null);
                          setExerciseFormData({});
                        }
                      } else {
                        console.log('Condition not met: selectedNode:', selectedNode, 'has children:', selectedNode?.children);
                      }
                    }}
                  />
                </>
              )}
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ProgramEditDialog; 